import { BooleanFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms.ts";
import { CompanyItem, isOk } from "@/common/common.ts";
import { TMessage } from "@/i18n/tt.ts";
import { DataTableProps, RowAction } from "@/components-v2/DataTable.tsx";
import {
  extractPortfolioShareholderUrl,
  SelectedPortfolioOption,
} from "@/portfolio/portfolioDomain.ts";
import {
  DCEvent,
  EventDispatcher,
  HttpEvent,
  httpPost,
  HttpResponse,
  navigateTo,
  showError,
} from "@/events/eventsDomain.ts";
import { DropdownMenuItemType, DropdownMenuProps } from "@/components-v2/Dropdown.tsx";
import { FileUploadConfig } from "@/fileUpload/fileUploadDomain.ts";
import { UploadShareholdersExcelResponse } from "@/api/ApiService.ts";
import { uploadShareholdersErrorMessage } from "@/types/excelImportDomain";
import { initContactsPage } from "../contactsDomain.ts";
import { isBlank, isNotBlank } from "@/utils/strings.ts";
import { getFullName } from "@/utils/Roles.ts";
import {
  AggregatedContactItem,
  AggregatedShareSummary,
  ContactsState,
  extractAggregatedShareholderDetailsUrl,
  InvestmentStatus,
  OwnershipItem,
  updateContactsState,
} from "../contactsDomain.ts";
import { TextWithTooltipProps } from "@/components-v2/TextWithTooltip.tsx";
import { totalNumberOfSharesOfShareholder } from "@/types/Shareholder.ts";
import { percentOf } from "@/utils/numbers.ts";
import { max } from "lodash";
import { toDate } from "@/utils/Dates.ts";
import { RootStoreData } from "@/events/rootStoreData.ts";

export type ShareholdersViewProps = {
  aggregatedPersonItems: AggregatedContactItem[];
  shareSummaries: AggregatedShareSummary[];
  searchFieldModel?: StringFieldModel;
  shareholderCompanyFilter?: RadioFieldModel<CompanyItem | null>;
  investmentStatusModel?: RadioFieldModel<InvestmentStatus>;
  navigateToDetails: (row: AggregatedContactItem) => void;
  filterMissingEmailModel?: BooleanFieldModel;
  filterMissingEmailLabel?: TMessage;
  isFilterMissingEmailDisabled?: boolean;
  rowActions: DataTableProps<AggregatedContactItem>["rowActions"];
  rowFilter: (row: AggregatedContactItem) => boolean;
  editWithExcelMenu?: DropdownMenuProps;
  excelResult?: UploadShareholdersExcelResponse;
  onCloseExcelResult: () => void;
};

export const shareholderApi = {
  uploadShareholderExcelEvents: ({
    portfolio,
    formData,
    onSuccess,
    onFailure,
  }: {
    portfolio: SelectedPortfolioOption;
    formData: FormData;
    onSuccess: (
      res: HttpResponse<UploadShareholdersExcelResponse>,
      state: RootStoreData,
    ) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): HttpEvent => {
    const url =
      portfolio.type === "portfolio-instance"
        ? `/portfolio/${portfolio.portfolio.id}/upload-shareholders`
        : `/portfolio/upload-shareholders`;
    return httpPost({
      url,
      data: formData,
      onSuccess,
      onFailure,
    });
  },
};

export const prepShareholdersView = ({
  state,
  dispatch,
  portfolio,
}: {
  state: ContactsState;
  dispatch: EventDispatcher;
  portfolio: SelectedPortfolioOption;
}): ShareholdersViewProps => {
  const { response } = state;
  const aggregatedPersonItems = isOk(response)
    ? response.data.contacts.filter((item) => item.ownershipItems.length > 0)
    : [];
  const shareSummaries = isOk(response) ? response.data.shareSummaries : [];
  const missingEmail = countMissingShareholderEmails(aggregatedPersonItems);

  return {
    aggregatedPersonItems,
    shareSummaries,
    searchFieldModel: state.searchFieldModel,
    shareholderCompanyFilter: state.shareholderCompanyFilter,
    investmentStatusModel: state.investmentStatusModel,
    navigateToDetails: (row) =>
      dispatch([{ type: "navigate", url: shareholderDetailsUrl(row, portfolio) }]),
    filterMissingEmailModel: state.shareholderMissingEmailModel,
    filterMissingEmailLabel: {
      key: "contacts.missing-email",
      params: { count: missingEmail },
    },
    isFilterMissingEmailDisabled: missingEmail === 0,
    rowActions: (row) => shareholderRowAction({ dispatch, row, portfolio }),
    rowFilter: (row) =>
      matchesShareholderItem(
        row,
        state.searchFieldModel?.inputValue?.toLowerCase().trim(),
        state.shareholderCompanyFilter?.value,
        state.investmentStatusModel?.value,
        state.shareholderMissingEmailModel?.value,
      ),
    editWithExcelMenu:
      state.canEditShareholdersExcel && state.importExcelConfig
        ? editWithExcelMenu({
            dispatch,
            portfolio,
            importExcelConfig: state.importExcelConfig,
            selectedCompany: state.shareholderCompanyFilter?.value ?? null,
          })
        : undefined,
    excelResult: state.shareholderExcelResult,
    onCloseExcelResult: () =>
      dispatch([updateContactsState(() => ({ shareholderExcelResult: undefined }))]),
  };
};

const editWithExcelMenu = ({
  dispatch,
  portfolio,
  importExcelConfig,
  selectedCompany,
}: {
  dispatch: EventDispatcher;
  portfolio: SelectedPortfolioOption;
  importExcelConfig: FileUploadConfig;
  selectedCompany: CompanyItem | null;
}): DropdownMenuProps => {
  return {
    button: { type: "link", label: { key: "excel.edit-with-excel" } },
    items: [
      {
        type: "item",
        icon: "download",
        label: { key: "excel.download" },
        onSelect: downloadShareholdersExcelEvent(portfolio, selectedCompany),
      },
      {
        type: "file-upload",
        label: { key: "excel.upload" },
        onSelect: (files) => dispatch(uploadShareholderExcelEvents(files, portfolio)),
        fileUploadConfig: importExcelConfig,
      },
      {
        type: "info-item",
        alert: { type: "info", icon: "info", message: { key: "shareholders.excel.info" } },
      },
    ],
  };
};

const shareholderRowAction = ({
  dispatch,
  row,
  portfolio,
}: {
  dispatch: EventDispatcher;
  row: AggregatedContactItem;
  portfolio: SelectedPortfolioOption;
}): RowAction[] => {
  return [
    [
      "context-menu",
      [
        {
          type: "item",
          label: { key: "contacts.company-roles.view-details" },
          icon: "user-circle",
          onSelect: () =>
            dispatch([{ type: "navigate", url: shareholderDetailsUrl(row, portfolio) }]),
        },
        { type: "label", label: { key: "contacts.company-roles-go-to-company" } },
        ...row.ownershipItems.map<DropdownMenuItemType>((o) => ({
          type: "item",
          label: o.company.companyName,
          icon: "eye",
          onSelect: () =>
            dispatch([
              navigateTo({
                name: "admin/shareholders-dashboard",
                params: { orgNo: o.company.organisationNumber },
              }),
            ]),
        })),
      ],
    ],
  ];
};

const downloadShareholdersExcelEvent = (
  portfolio: SelectedPortfolioOption,
  selectedCompany: CompanyItem | null,
): DCEvent[] => {
  const path =
    portfolio.type === "portfolio-instance"
      ? `/portfolio/${portfolio.portfolio.id}/export-shareholders`
      : `/portfolio/export-shareholders`;
  const query = selectedCompany ? `?orgNo=${selectedCompany.organisationNumber}` : "";
  return [{ type: "download-file", url: `${path}${query}` }];
};

const uploadShareholderExcelEvents = (
  files: File[] | null,
  portfolio: SelectedPortfolioOption,
): DCEvent[] => {
  if (!files) return [];
  const formData = new FormData();
  for (const file of files) formData.append("file", file);
  return [
    updateContactsState(() => ({ shareholderExcelResult: undefined })),
    shareholderApi.uploadShareholderExcelEvents({
      portfolio,
      formData,
      onSuccess: (res) => {
        const data = res.data as UploadShareholdersExcelResponse;
        return [
          updateContactsState(() => ({ shareholderExcelResult: data })),
          ...initContactsPage(portfolio),
        ];
      },
      onFailure: (err) => [showError(uploadShareholdersErrorMessage(err))],
    }),
  ];
};

export const matchesShareholderItem = (
  item: AggregatedContactItem,
  filterInput?: string,
  selectedCompany?: CompanyItem | null,
  investmentStatus?: InvestmentStatus,
  missingEmailOnly?: boolean,
): boolean => {
  const representative = shareholderRepresentative(item.ownershipItems)?.toLowerCase();
  const textMatches =
    !filterInput ||
    item.name.toLowerCase().includes(filterInput) ||
    item.ownershipItems.some((o) => o.company.companyName.toLowerCase().includes(filterInput)) ||
    (representative !== undefined && representative.includes(filterInput));
  const companyMatches =
    !selectedCompany ||
    item.ownershipItems.some(
      (o) => o.company.organisationNumber === selectedCompany.organisationNumber,
    );
  const statusMatches =
    !investmentStatus ||
    investmentStatus === "all" ||
    matchesInvestmentStatus(item.ownershipItems, investmentStatus);
  const emailMatches = !missingEmailOnly || countMissingShareholderEmails([item]) > 0;
  return textMatches && companyMatches && statusMatches && emailMatches;
};

const matchesInvestmentStatus = (
  ownershipItems: OwnershipItem[],
  status: InvestmentStatus,
): boolean => {
  const isCurrentShareholder = ownershipItems.some(
    (o) => totalNumberOfSharesOfShareholder(o.shareholder) > 0,
  );
  switch (status) {
    case "current":
      return isCurrentShareholder;
    case "former":
      return !isCurrentShareholder;
    case "all":
      return true;
  }
};

export type ShareholderCompanyEntry = {
  companyName: string;
  percent: number;
};

export function shareholderRepresentative(ownershipItems: OwnershipItem[]): string | undefined {
  const name = getFullName(
    ownershipItems.find((o) => o.shareholder.isCompany)?.shareholder.contactInfo,
  );
  return name || undefined;
}

export function shareholderCompanyEntries(
  ownershipItems: OwnershipItem[],
  shareSummaries: AggregatedShareSummary[],
): ShareholderCompanyEntry[] {
  return ownershipItems.map((o) => {
    const totalShares = shareSummaries
      .filter((s) => s.orgNo === o.company.organisationNumber)
      .reduce((sum, s) => sum + s.numberOfShares, 0);
    return {
      companyName: o.company.companyName,
      percent: percentOf(totalNumberOfSharesOfShareholder(o.shareholder), totalShares),
    };
  });
}

export function shareholderLastLogin(ownershipItems: OwnershipItem[]): string | undefined {
  const dates = ownershipItems
    .map((o) => o.shareholder.lastLogin)
    .filter(isNotBlank)
    .map(toDate);
  return max(dates)?.toISOString();
}

export function shareholderEmail(ownershipItems: OwnershipItem[]): TextWithTooltipProps {
  const emails = ownershipItems.map((o) => o.shareholder.email);
  const areAllEmailsSame = emails.length > 0 && emails.every((e) => e === emails[0]);
  const missingEmails = ownershipItems.filter((o) => isBlank(o.shareholder.email));

  if (missingEmails.length === ownershipItems.length) {
    return {
      tooltip: {
        key: "contacts.email-not-set",
        params: { count: missingEmails.length },
      },
      text: { key: "not-set" },
    };
  } else if (areAllEmailsSame) {
    return { text: { translated: emails[0] ?? "" } };
  } else if (missingEmails.length > 0) {
    return {
      tooltip: {
        key: "contacts.incomplete-email",
        params: {
          count: missingEmails.length,
          name: missingEmails.length === 1 ? missingEmails[0].company.companyName : undefined,
        },
      },
      text: { key: "contacts.incomplete" },
    };
  }
  return {
    tooltip: { key: "contacts.multiple-emails-found" },
    text: { key: "contacts.multiple" },
  };
}

const countMissingShareholderEmails = (items: AggregatedContactItem[]): number =>
  items.reduce(
    (acc, x) => acc + x.ownershipItems.filter((o) => isBlank(o.shareholder.email)).length,
    0,
  );

export const shareholderDetailsUrl = (
  row: AggregatedContactItem,
  portfolio: SelectedPortfolioOption,
): string => {
  const shareholderId = row.ownershipItems[0].shareholder.id;
  return row.ownershipItems.length === 1 && row.roleItems.length === 0
    ? extractPortfolioShareholderUrl(portfolio, shareholderId)
    : extractAggregatedShareholderDetailsUrl(portfolio, shareholderId);
};
